(() => {
  'use strict';

  const selectGalleryImage = (root, index) => {
    root.querySelectorAll('[data-gallery-slide]').forEach((slide) => {
      slide.classList.toggle('is-active', slide.dataset.gallerySlide === index);
    });
    root.querySelectorAll('[data-gallery-thumb]').forEach((thumb) => {
      const active = thumb.dataset.galleryThumb === index;
      thumb.classList.toggle('is-active', active);
      thumb.setAttribute('aria-current', active ? 'true' : 'false');
    });
  };

  const keepActiveThumbnailVisible = (root, index) => {
    const strip = root.querySelector('[data-gallery-thumbs]');
    const thumb = root.querySelector(`[data-gallery-thumb="${index}"]`);
    if (!strip || !thumb) return;
    const stripRect = strip.getBoundingClientRect();
    const thumbRect = thumb.getBoundingClientRect();
    if (thumbRect.left < stripRect.left) {
      strip.scrollBy({ left: thumbRect.left - stripRect.left, behavior: 'smooth' });
    } else if (thumbRect.right > stripRect.right) {
      strip.scrollBy({ left: thumbRect.right - stripRect.right, behavior: 'smooth' });
    }
  };

  const initGallery = (root) => {
    const gallery = root.querySelector('[data-gallery]');
    const thumbs = root.querySelector('[data-gallery-thumbs]');
    if (!gallery || root.dataset.galleryReady === 'true') return;
    root.dataset.galleryReady = 'true';
    root.addEventListener('click', (event) => {
      const thumb = event.target.closest('[data-gallery-thumb]');
      if (thumb) {
        selectGalleryImage(root, thumb.dataset.galleryThumb);
        keepActiveThumbnailVisible(root, thumb.dataset.galleryThumb);
        return;
      }
      const nav = event.target.closest('[data-gallery-nav]');
      if (!nav) return;
      const allThumbs = [...root.querySelectorAll('[data-gallery-thumb]')];
      const activeIndex = Math.max(0, allThumbs.findIndex((item) => item.classList.contains('is-active')));
      const nextIndex = Math.min(allThumbs.length - 1, Math.max(0, activeIndex + Number(nav.dataset.galleryNav)));
      selectGalleryImage(root, String(nextIndex));
      keepActiveThumbnailVisible(root, String(nextIndex));
    });
  };

  const initModal = (root) => {
    const modal = root.querySelector('[data-size-guide]');
    const open = root.querySelector('[data-size-guide-open]');
    const close = root.querySelector('[data-size-guide-close]');
    if (!modal || !open || modal.dataset.ready === 'true') return;
    modal.dataset.ready = 'true';
    open.addEventListener('click', () => modal.showModal());
    close?.addEventListener('click', () => modal.close());
    modal.addEventListener('click', (event) => {
      if (event.target === modal) modal.close();
    });
  };

  const initTabs = (root) => {
    root.querySelectorAll('[data-pdp-tabs]').forEach((tabsRoot) => {
      if (tabsRoot.dataset.ready === 'true') return;
      tabsRoot.dataset.ready = 'true';
      const tabs = [...tabsRoot.querySelectorAll('[role="tab"]')];
      if (!tabs.length) return;

      const activate = (activeTab) => {
        tabs.forEach((tab) => {
          const selected = tab === activeTab;
          tab.setAttribute('aria-selected', selected ? 'true' : 'false');
          tab.tabIndex = selected ? 0 : -1;
          const panel = tabsRoot.querySelector(`#${CSS.escape(tab.getAttribute('aria-controls'))}`);
          if (panel) panel.hidden = !selected;
        });
      };

      const moveFocus = (currentTab, direction) => {
        const currentIndex = tabs.indexOf(currentTab);
        const nextIndex = (currentIndex + direction + tabs.length) % tabs.length;
        tabs.forEach((tab, index) => { tab.tabIndex = index === nextIndex ? 0 : -1; });
        tabs[nextIndex].focus();
      };

      tabs.forEach((tab) => {
        tab.addEventListener('click', () => activate(tab));
        tab.addEventListener('keydown', (event) => {
          if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
            event.preventDefault();
            moveFocus(tab, event.key === 'ArrowRight' ? 1 : -1);
          } else if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
            event.preventDefault();
            activate(tab);
          }
        });
      });
    });
  };

  const initBuilder = (root) => {
    const builder = root.querySelector('[data-builder]');
    const inlineButton = root.querySelector('[data-add-to-cart]');
    const mobileBar = root.querySelector('[data-mobile-atc]');
    const mobileButton = root.querySelector('[data-mobile-atc-button]');
    const message = root.querySelector('[data-builder-message]');
    if (!builder || !inlineButton || builder.dataset.ready === 'true') return;
    builder.dataset.ready = 'true';

    const rows = [...builder.querySelectorAll('[data-size-row]')];
    const totalOutput = builder.querySelector('[data-total-count]');
    const mobileCount = root.querySelector('[data-mobile-count]');
    const gift = builder.querySelector('[data-gift-state]');
    const giftCopy = builder.querySelector('[data-gift-copy]');
    const giftHeading = builder.querySelector('[data-gift-heading]');
    const giftLocked = builder.querySelector('[data-gift-locked]');
    const giftUnlocked = builder.querySelector('[data-gift-unlocked]');
    const giftThreshold = Number(builder.dataset.giftThreshold || 0);
    const giftVariantId = builder.dataset.giftVariantId.trim();
    const bundleIncentive = builder.querySelector('[data-bundle-incentive]');
    const bundleIncentiveCopy = builder.querySelector('[data-bundle-incentive-copy]');
    const bundleThreshold = Number(builder.dataset.bundleThreshold || 0);
    const bundleSaving = builder.dataset.bundleSaving.trim();
    const giftLabel = builder.dataset.giftLabel.trim();
    const giftUnlockedLabel = builder.dataset.giftUnlockedLabel.trim();
    const atcPriceGroup = inlineButton.querySelector('[data-atc-price-group]');
    const atcPrice = inlineButton.querySelector('[data-atc-price]');
    const atcCompare = inlineButton.querySelector('[data-atc-compare]');
    const mobilePriceGroup = mobileButton?.querySelector('[data-mobile-atc-price-group]');
    const mobilePrice = mobileButton?.querySelector('[data-mobile-atc-price]');

    const parseMoney = (value) => {
      const source = String(value || '').trim();
      const match = source.match(/^(.*?)(-?[\d\s.,]+)([^\d]*)$/);
      if (!match) return null;
      const raw = match[2].replace(/\s/g, '');
      const comma = raw.lastIndexOf(',');
      const dot = raw.lastIndexOf('.');
      const decimalMark = comma > dot ? ',' : dot > comma ? '.' : null;
      let normalized = raw;
      let decimals = 0;
      if (decimalMark) {
        const decimalIndex = raw.lastIndexOf(decimalMark);
        const trailing = raw.length - decimalIndex - 1;
        if (trailing === 2) {
          decimals = 2;
          normalized = raw.slice(0, decimalIndex).replace(/[.,]/g, '') + '.' + raw.slice(decimalIndex + 1);
        } else {
          normalized = raw.replace(/[.,]/g, '');
        }
      }
      const amount = Number(normalized);
      if (!Number.isFinite(amount)) return null;
      return { amount, decimals, prefix: match[1], suffix: match[3] };
    };

    const formatMoney = (amount, pattern) => {
      const number = amount.toLocaleString('nl-NL', {
        minimumFractionDigits: pattern.decimals,
        maximumFractionDigits: pattern.decimals,
      });
      return `${pattern.prefix}${number}${pattern.suffix}`;
    };

    const setBundleMessage = (prefix, saving) => {
      const strong = document.createElement('strong');
      strong.textContent = saving;
      bundleIncentiveCopy.replaceChildren(document.createTextNode(prefix), strong);
    };

    const selected = () => rows.map((row) => ({
      row,
      id: row.dataset.variantId.trim(),
      label: row.dataset.label,
      price: parseMoney(row.dataset.priceText),
      compareAtPrice: parseMoney(row.dataset.compareAtPriceText),
      quantity: Number(row.querySelector('[data-quantity]').value || row.querySelector('[data-quantity]').textContent || 0),
    })).filter((item) => item.quantity > 0);

    const update = () => {
      const items = selected();
      const total = items.reduce((sum, item) => sum + item.quantity, 0);
      const configured = total > 0 && items.every((item) => /^\d+$/.test(item.id));
      const pricedItems = items.filter((item) => item.price);
      const hasCompletePrice = items.length > 0 && pricedItems.length === items.length;
      const pricePattern = pricedItems[0]?.price;
      const priceTotal = hasCompletePrice
        ? items.reduce((sum, item) => sum + (item.price.amount * item.quantity), 0)
        : 0;
      const compareTotal = hasCompletePrice
        ? items.reduce((sum, item) => sum + ((item.compareAtPrice || item.price).amount * item.quantity), 0)
        : 0;
      if (totalOutput) totalOutput.textContent = `${total} geselecteerd`;
      if (mobileCount) mobileCount.textContent = `${total} geselecteerd`;
      if (atcPriceGroup && atcPrice) {
        atcPriceGroup.hidden = !hasCompletePrice;
        atcPrice.textContent = hasCompletePrice ? formatMoney(priceTotal, pricePattern) : '';
      }
      if (atcCompare) {
        const showCompare = hasCompletePrice && compareTotal > priceTotal;
        atcCompare.hidden = !showCompare;
        atcCompare.textContent = showCompare ? formatMoney(compareTotal, pricePattern) : '';
      }
      if (mobilePriceGroup && mobilePrice) {
        mobilePriceGroup.hidden = !hasCompletePrice;
        mobilePrice.textContent = hasCompletePrice ? formatMoney(priceTotal, pricePattern) : '';
      }
      inlineButton.disabled = !configured;
      if (mobileButton) mobileButton.disabled = !configured;
      if (bundleIncentive && bundleIncentiveCopy && bundleThreshold > 0 && bundleSaving) {
        const remaining = Math.max(0, bundleThreshold - total);
        const unlocked = remaining === 0;
        bundleIncentive.classList.toggle('is-unlocked', unlocked);
        setBundleMessage(
          unlocked ? 'Je bespaart ' : `Voeg nog ${remaining} ${remaining === 1 ? 'plank' : 'planken'} toe en `,
          bundleSaving,
        );
      }
      if (gift && giftCopy && giftThreshold > 0) {
        const unlocked = total >= giftThreshold;
        gift.classList.toggle('is-unlocked', unlocked);
        giftCopy.textContent = `Voeg nog ${Math.max(0, giftThreshold - total)} ${Math.max(0, giftThreshold - total) === 1 ? 'plank' : 'planken'} toe voor je gratis antislipmat`;
        if (giftHeading) giftHeading.textContent = unlocked ? giftUnlockedLabel : giftLabel;
        if (giftLocked) giftLocked.hidden = unlocked;
        if (giftUnlocked) giftUnlocked.hidden = !unlocked;
      }
      if (message) {
        message.classList.remove('is-error');
        if (total > 0 && !configured) {
          message.hidden = false;
          message.textContent = 'Deze uitvoering is momenteel niet beschikbaar.';
        } else {
          message.hidden = true;
          message.textContent = '';
        }
      }
    };

    builder.addEventListener('click', (event) => {
      const step = event.target.closest('[data-step]');
      if (!step) return;
      const row = step.closest('[data-size-row]');
      const output = row.querySelector('[data-quantity]');
      const next = Math.max(0, Number(output.value || output.textContent || 0) + Number(step.dataset.step));
      output.value = next;
      output.textContent = next;
      update();
    });

    const addSelection = async () => {
      const items = selected();
      if (!items.length || items.some((item) => !/^\d+$/.test(item.id))) {
        update();
        return;
      }
      inlineButton.disabled = true;
      if (mobileButton) mobileButton.disabled = true;
      const label = inlineButton.querySelector('[data-atc-label]');
      const original = label.textContent;
      label.textContent = 'Toevoegen…';
      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [
              ...items.map((item) => ({ id: Number(item.id), quantity: item.quantity })),
              ...(giftThreshold > 0 && items.reduce((sum, item) => sum + item.quantity, 0) >= giftThreshold && /^\d+$/.test(giftVariantId)
                ? [{ id: Number(giftVariantId), quantity: 1, properties: { _free_gift: 'Antislipmat' } }]
                : []),
            ],
          }),
        });
        if (!response.ok) throw new Error('Cart request failed');
        label.textContent = 'Toegevoegd';
        window.setTimeout(() => { window.location.assign('/cart'); }, 450);
      } catch (error) {
        label.textContent = original;
        inlineButton.disabled = false;
        if (mobileButton) mobileButton.disabled = false;
        if (message) {
          message.hidden = false;
          message.classList.add('is-error');
          message.textContent = 'De winkelwagen kon niet worden bijgewerkt. Probeer het opnieuw.';
        }
      }
    };

    inlineButton.addEventListener('click', addSelection);
    mobileButton?.addEventListener('click', addSelection);
    update();

    if (mobileBar && 'IntersectionObserver' in window) {
      const observer = new IntersectionObserver(([entry]) => {
        const hasPassedInlineButton = entry.boundingClientRect.top < 0;
        mobileBar.hidden = entry.isIntersecting || !hasPassedInlineButton || window.innerWidth >= 750;
      }, { threshold: 0.05 });
      observer.observe(inlineButton);
      window.addEventListener('resize', () => {
        if (window.innerWidth >= 750) mobileBar.hidden = true;
      }, { passive: true });
    }
  };

  const init = (scope = document) => {
    scope.querySelectorAll('[data-cwpdp-main]').forEach((root) => {
      initGallery(root);
      initModal(root);
      initTabs(root);
      initBuilder(root);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
